import { useEffect, useState } from 'react';
import { IconButton, InputBase, Select as MSelect, MenuItem, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { FORTA_BLACK, FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../utils/colors';
import { getThemeColor } from '../../utils/common';
import { BackgroundTypes, DEFAULT_INPUT_HEIGHT, Themes } from '../../constants';

import type {
  InputBaseProps as MInputBaseProps,
  SelectProps as MSelectProps,
  SelectChangeEvent,
} from '@mui/material';
import type { ReactNode } from 'react';
import type { ISelectOption, SelectValueType } from '../../utils/types';

interface SelectProps extends MSelectProps {
  colorTheme?: Themes;
  backgroundType?: BackgroundTypes;
  error?: boolean;
  helperText?: ReactNode;
  inputHeight?: number;
  popoverWidth?: number;
  autoHeight?: boolean;
  options: readonly ISelectOption[];
  menuProps?: any;
  optionDataTestIdPrefix?: string;
  selectFirstOption?: boolean;
  clearable?: boolean;
  helperTextAlign?: 'right' | 'left';
  onUpdate: (value: SelectValueType) => void;
  onClear?: () => void;
}

interface InputBaseProps extends MInputBaseProps {
  colorTheme?: Themes;
  coloredBackground?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  inputHeight?: number;
  autoHeight?: boolean;
}

const StyledSelect = styled(InputBase, {
  shouldForwardProp: (prop) =>
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'error' &&
    prop !== 'inputHeight' &&
    prop !== 'autoHeight' &&
    prop !== 'backgroundType' &&
    prop !== 'popoverWidth' &&
    prop !== 'optionDataTestIdPrefix' &&
    prop !== 'onUpdate' &&
    prop !== 'helperTextAlign' &&
    prop != 'helperText',
})<InputBaseProps>(
  ({ theme, colorTheme, coloredBackground, error, inputHeight, autoHeight, sx = {} }) => {
    const selectedTheme = colorTheme ?? Themes.Primary;

    return ({
    position: 'relative',
    height: autoHeight ? 'fit-content' : inputHeight,
    '& .MuiSelect-select.MuiInputBase-input': {
      position: 'relative',
      backgroundColor: coloredBackground ? getThemeColor(selectedTheme).light3 : FORTA_WHITE,
      boxSizing: 'border-box',
      color: FORTA_BLACK,
      borderRadius: 8,
      padding: '0 2em 0 1em',
      height: '100%',
      fontSize: 16,
      textAlign: 'left',
      border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
      '&:focus-within': {
        border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
      },

      '&, & > *': {
        textOverflow: autoHeight ? 'none' : 'ellipsis',
        whiteSpace: autoHeight ? 'normal' : 'no-wrap',
        overflow: 'hidden',
        lineHeight: autoHeight ? '1.5em' : `${inputHeight}px`,
        minHeight: autoHeight ? 40 : 0,
        margin: autoHeight ? '10px 0' : '0',
      },
      '&:focus': {
        borderRadius: 8,
      },
      '&.Mui-disabled': {
        WebkitTextFillColor: FORTA_BLACK,
      },
      ...sx,
    },
    '& .MuiSvgIcon-root': {
      position: 'absolute',
      right: theme.spacing(1),
      color: getThemeColor(selectedTheme).primary,
    },
    '&.Mui-disabled .MuiSvgIcon-root': {
      color: getThemeColor(selectedTheme).light2,
    },
    });
  },
);

const FHSelect = (props: SelectProps) => {
  const {
    value,
    options = [],
    colorTheme = Themes.Primary,
    backgroundType = BackgroundTypes.White,
    inputHeight = DEFAULT_INPUT_HEIGHT,
    popoverWidth,
    error = false,
    autoHeight = false,
    clearable = false,
    optionDataTestIdPrefix = '',
    disabled,
    renderValue,
    onUpdate,
    onClear,
    helperTextAlign = 'right',
    selectFirstOption = true,
    menuProps = {},
    helperText,
    ...mSelectProps
  } = props;

  const MenuProps = {
    sx: {
      '& .MuiPaper-root': {
        padding: '10px 0 10px 0',
        borderRadius: '8px',
        backgroundColor:
          backgroundType !== BackgroundTypes.White
            ? getThemeColor(colorTheme).light3
            : FORTA_WHITE,
        width: popoverWidth ? popoverWidth : 'auto',
      },
      '& .MuiMenuItem-root': {
        fontSize: 16,
        whiteSpace: 'normal',

        '&:nth-of-type(even)':
          backgroundType === BackgroundTypes.EvenColored
            ? {
                backgroundColor: FORTA_WHITE,
              }
            : {},
      },
      '& .MuiMenuItem-root:hover': {
        backgroundColor: getThemeColor(colorTheme).primary,
        color: FORTA_WHITE,
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: `${getThemeColor(colorTheme).primary} !important`,
        color: FORTA_WHITE,
      },
      '& .MuiMenuItem-root.Mui-selected:hover': {
        backgroundColor: getThemeColor(colorTheme).primary,
      },
      '& ::-webkit-scrollbar-thumb': {
        background: getThemeColor(colorTheme).primary,
      },
      '& ::-webkit-scrollbar-track': {
        background: getThemeColor(colorTheme).light3,
      },
      ...menuProps,
    },
    MenuListProps: {
      style: {
        padding: 0,
      },
    },
  };

  const [defaultOption, setDefaultOption] = useState<SelectValueType>('');

  const handleClear = () => {
    if (disabled) {
      return;
    }

    onClear?.();
  };

  useEffect(() => {
    if (!options || options.length !== 1 || disabled || !selectFirstOption) {
      setDefaultOption('');
      return;
    }

    setDefaultOption(options[0].value);
    if (!value || value !== options[0].value) onUpdate(options[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value, disabled]);

  return (
    <>
      <MSelect
        {...mSelectProps}
        disabled={disabled}
        value={options && options.length ? (value === 0 || value ? value : defaultOption) : ''}
        onKeyDown={(e: any) => {
          // Prevent parent containers (e.g., MUI Menu/List) from intercepting keystrokes
          // which can cause focus to shift away from the input when typing.
          e.stopPropagation();
          if (mSelectProps?.onKeyDown) mSelectProps.onKeyDown(e);
        }}
        input={
          <StyledSelect
            autoHeight={autoHeight}
            colorTheme={colorTheme}
            coloredBackground={backgroundType !== BackgroundTypes.White}
            error={error}
            inputHeight={inputHeight}
          />
        }
        IconComponent={() => (
          <KeyboardArrowDownIcon
            sx={{
              position: 'absolute !important',
              right: '6px !important',
              pointerEvents: 'none !important',
            }}
          />
        )}
        MenuProps={MenuProps}
        renderValue={defaultOption ? undefined : renderValue}
        onChange={(e: SelectChangeEvent<unknown>) => {
          const selectedOption = options.find((option) => option.value === e.target.value);
          if (selectedOption) {
            onUpdate(selectedOption.value);
          }
        }}
      >
        {options.map((option: ISelectOption) => (
          <MenuItem
            key={option.value}
            value={option.value}
            data-value={option.value}
            disabled={option.disabled ?? false}
            data-testid={`${optionDataTestIdPrefix}_${String(option.value).replace(/\s+/g, '_')}`}
          >
            {option.label}
          </MenuItem>
        ))}
      </MSelect>
      {clearable && (
        <IconButton
          sx={{ position: 'absolute', right: 30, padding: 0, top: 10 }}
          disabled={disabled}
          onClick={handleClear}
        >
          <Close sx={{ width: 18, height: 18, mt: 0.2 }} />
        </IconButton>
      )}
      {error && (
        <Typography textAlign={helperTextAlign} sx={{ color: 'red', fontSize: '14px' }}>
          {helperText}
        </Typography>
      )}
    </>
  );
};

export default FHSelect;
